import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cloudRunsApi, type CloudRunTarget } from "@/lib/api/cloud-runs";

/** `all` prefixes every cloud-run status query, so one invalidation refreshes them all. */
export const cloudRunKeys = {
  all: ["cloud-runs"] as const,
  active: ["cloud-runs", "active"] as const,
  status: (target: CloudRunTarget = {}) =>
    ["cloud-runs", "status", target.epicRequestId ?? null, target.epicId ?? null] as const,
};

/**
 * Whether this person can start a CompBox cloud run, why not (`reason`) and
 * the organization's cloud hours this month. Pass the epic request or epic the
 * run is for to also check that its project's repository is connected.
 */
export function useCloudRunStatus(target: CloudRunTarget = {}, enabled = true) {
  return useQuery({
    queryKey: cloudRunKeys.status(target),
    queryFn: async () => (await cloudRunsApi.status(target)).data,
    staleTime: 60_000,
    enabled,
  });
}

/** Cloud runs currently running or queued for the organization; polls while the tab is visible. */
export function useActiveCloudRuns(options: { refetchInterval?: number | false; enabled?: boolean } = {}) {
  const { refetchInterval = 30_000, enabled = true } = options;
  return useQuery({
    queryKey: cloudRunKeys.active,
    queryFn: async () => (await cloudRunsApi.active()).data,
    staleTime: 15_000,
    refetchInterval,
    refetchIntervalInBackground: false,
    enabled,
  });
}

/** Plan or build in CompBox cloud (POST /cloud-runs); refreshes the hours after. */
export function useStartCloudRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof cloudRunsApi.start>[0]) => (await cloudRunsApi.start(input)).data,
    onSettled: () => queryClient.invalidateQueries({ queryKey: cloudRunKeys.all }),
  });
}
